import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material'
import { alpha, keyframes, useTheme } from '@mui/material/styles'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import ScienceIcon from '@mui/icons-material/Science'
import VerifiedIcon from '@mui/icons-material/Verified'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import LockIcon from '@mui/icons-material/Lock'
import SchoolIcon from '@mui/icons-material/School'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import BusinessIcon from '@mui/icons-material/Business'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import FactoryIcon from '@mui/icons-material/Factory'
import PublicIcon from '@mui/icons-material/Public'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import GitHubIcon from '@mui/icons-material/GitHub'
import CancelIcon from '@mui/icons-material/Cancel'
import SensorsIcon from '@mui/icons-material/Sensors'
import { tokens } from '../theme'
import stats from '../data/stats.json'
import aboutStats from '../data/aboutStats.json'
import { ContextCard, KPICard } from '../components/ui/cards'
import StatCard from '../components/ui/StatCard'

// =============================================================================
// ANIMATIONS
// =============================================================================

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`

const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(160, 206, 78, 0.3);
  }
  50% {
    box-shadow: 0 0 60px rgba(160, 206, 78, 0.5);
  }
`

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`

// =============================================================================
// ANIMATED COUNTER COMPONENT
// =============================================================================

function AnimatedCounter({ end, duration = 2000, suffix = '', prefix = '', decimals = 0 }) {
  const [count, setCount] = useState(0)
  const countRef = useRef(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.3 }
    )

    if (countRef.current) {
      observer.observe(countRef.current)
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!isVisible) return

    const startTime = Date.now()
    const endValue = parseFloat(end)

    const animate = () => {
      const now = Date.now()
      const progress = Math.min((now - startTime) / duration, 1)

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4)
      const current = endValue * easeOutQuart

      setCount(current)

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    requestAnimationFrame(animate)
  }, [end, duration, isVisible])

  return (
    <span ref={countRef}>
      {prefix}{count.toFixed(decimals)}{suffix}
    </span>
  )
}

// =============================================================================
// STAT CARD COMPONENT (from About page)
// =============================================================================

function BigStatCard({ value, label, sublabel, icon: Icon, color = 'primary' }) {
  const theme = useTheme()
  return (
    <Card
      sx={{
        height: '100%',
        textAlign: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.05)} 100%)`,
        border: `1px solid ${alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.2)}`,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 24px ${alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.2)}`
        }
      }}
    >
      <CardContent sx={{ py: 3 }}>
        {Icon && (
          <Icon sx={{ fontSize: 32, color: theme.palette[color]?.main || theme.palette.primary.main, mb: 1 }} />
        )}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            color: theme.palette[color]?.main || theme.palette.primary.main,
            mb: 0.5,
            fontFamily: 'Outfit, sans-serif'
          }}
        >
          {value}
        </Typography>
        <Typography variant="body1" fontWeight={600} color="text.primary">
          {label}
        </Typography>
        {sublabel && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {sublabel}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

// =============================================================================
// VALIDATION BADGE COMPONENT (from About page)
// =============================================================================

function ValidationBadge({ icon: Icon, title, subtitle, count }) {
  const theme = useTheme()
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Icon sx={{ color: 'white', fontSize: 24 }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          {count && <>{count} </>}{title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
    </Box>
  )
}

// =============================================================================
// LANDING PAGE COMPONENT
// =============================================================================

export default function Landing() {
  const navigate = useNavigate()
  const [loaded, setLoaded] = useState(false)
  const theme = useTheme()
  const palette = theme.palette

  useEffect(() => {
    setLoaded(true)
  }, [])

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background gradient orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '60%',
          height: '60%',
          background: `radial-gradient(circle, ${alpha(palette.primary.main, 0.15)} 0%, transparent 70%)`,
          filter: 'blur(60px)',
          animation: `${float} 8s ease-in-out infinite`,
          pointerEvents: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-30%',
          left: '-20%',
          width: '70%',
          height: '70%',
          background: `radial-gradient(circle, ${alpha(palette.accent.teal, 0.1)} 0%, transparent 70%)`,
          filter: 'blur(80px)',
          animation: `${float} 10s ease-in-out infinite reverse`,
          pointerEvents: 'none'
        }}
      />

      {/* Noise texture overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.03,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          pointerEvents: 'none'
        }}
      />

      {/* Main content */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 8, md: 12 } }}>

        {/* Hero Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: { xs: 4, md: 6 }
          }}
        >
          {/* Overline */}
          <Typography
            variant="overline"
            sx={{
              color: palette.primary.main,
              fontSize: '0.75rem',
              letterSpacing: '0.2em',
              mb: 2,
              opacity: loaded ? 1 : 0,
              animation: loaded ? `${fadeInUp} 0.6s ease-out` : 'none'
            }}
          >
            PermaFrost NMR Technology
          </Typography>

          {/* Main headline */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '5rem', lg: '6rem' },
              fontWeight: 800,
              lineHeight: 1.05,
              letterSpacing: '-0.04em',
              mb: 3,
              maxWidth: '900px',
              opacity: loaded ? 1 : 0,
              animation: loaded ? `${fadeInUp} 0.8s ease-out 0.1s both` : 'none'
            }}
          >
            Lab & Engineering{' '}
            <Box
              component="span"
              sx={{
                background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.accent.teal} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Corpus
            </Box>
          </Typography>

          {/* Subheadline */}
          <Typography
            variant="h5"
            sx={{
              color: 'text.secondary',
              fontWeight: 400,
              lineHeight: 1.6,
              maxWidth: '600px',
              mb: 5,
              fontSize: { xs: '1rem', md: '1.25rem' },
              opacity: loaded ? 1 : 0,
              animation: loaded ? `${fadeInUp} 0.8s ease-out 0.2s both` : 'none'
            }}
          >
            15+ years of comprehensive performance data from 6 independent labs,
            PE-certified field studies, and 300,000+ installations
            across 38 countries.
          </Typography>

          {/* CTA Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              mb: 8,
              opacity: loaded ? 1 : 0,
              animation: loaded ? `${fadeInUp} 0.8s ease-out 0.3s both` : 'none'
            }}
          >
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/dashboard')}
              sx={{
                px: 4,
                py: 1.75,
                fontSize: '1.0625rem',
                animation: `${glow} 3s ease-in-out infinite`,
                '&:hover': {
                  animation: 'none',
                  boxShadow: tokens.shadows.glow.primary
                }
              }}
            >
              Explore Dashboard
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/case-studies')}
              sx={{
                px: 4,
                py: 1.75,
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': {
                  borderColor: palette.primary.main,
                  bgcolor: alpha(palette.primary.main, 0.1)
                }
              }}
            >
              View Case Studies
            </Button>
          </Box>

          {/* Stats Grid — Card Variety */}
          <Grid
            container
            spacing={3}
            sx={{
              opacity: loaded ? 1 : 0,
              animation: loaded ? `${fadeInUp} 0.8s ease-out 0.4s both` : 'none'
            }}
          >
            <Grid item xs={12} sm={6} md={3} sx={{ animation: `${fadeInUp} 0.6s ease-out 0.5s both` }}>
              <ContextCard
                title="Average Efficiency Gain"
                value={stats.mean_efficiency || 25.2}
                displayValue={`${(stats.mean_efficiency || 25.2).toFixed(1)}%`}
                benchmarkMin={0}
                benchmarkMax={60}
                benchmarkValue={15}
                benchmarkLabel="Industry Avg"
                subtitle="PE-certified field measurements"
                icon={<TrendingUpIcon />}
                color={palette.primary.main}
                unit="%"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ animation: `${fadeInUp} 0.6s ease-out 0.6s both` }}>
              <StatCard
                title="Independent Measurements"
                value={stats.total_measurements || 458}
                subtitle="Across 6 independent laboratories"
                icon={<ScienceIcon />}
                color={palette.accent.teal}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ animation: `${fadeInUp} 0.6s ease-out 0.7s both` }}>
              <StatCard
                title="Case Studies"
                value={stats.total_case_studies || 133}
                subtitle="PE-certified field studies"
                icon={<VerifiedIcon />}
                color={palette.accent.blue}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ animation: `${fadeInUp} 0.6s ease-out 0.8s both` }}>
              <StatCard
                title="Geographic Coverage"
                value={`${stats.us_states || 24} States`}
                subtitle={`${stats.countries || 6} countries, ${stats.total_locations || 133} sites`}
                icon={<LocationOnIcon />}
                color={palette.accent.teal}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Social Proof Strip */}
        <Box
          sx={{
            py: 3,
            textAlign: 'center',
            opacity: loaded ? 1 : 0,
            animation: loaded ? `${fadeIn} 0.8s ease-out 1s both` : 'none'
          }}
        >
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.15em', fontSize: '0.7rem' }}>
            Validated across Fortune 500 companies in
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 2, md: 3 }, mt: 1.5, flexWrap: 'wrap' }}>
            {['Banking', 'Telecom', 'Retail', 'Hospitality', 'Healthcare', 'Data Centers', 'Government'].map((industry) => (
              <Typography
                key={industry}
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  letterSpacing: '0.05em'
                }}
              >
                {industry}
              </Typography>
            ))}
          </Box>
        </Box>

        {/* Features Section */}
        <Box sx={{ py: { xs: 5, md: 8 } }}>
          <Typography
            variant="overline"
            sx={{
              color: palette.accent.teal,
              letterSpacing: '0.15em',
              mb: 2,
              display: 'block'
            }}
          >
            Why This Data Matters
          </Typography>

          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              mb: 3,
              maxWidth: '800px'
            }}
          >
            The most comprehensive HVAC efficiency dataset ever compiled
          </Typography>

          <Grid container spacing={4}>
            {[
              {
                title: 'Independent Validation',
                description: '6 independent laboratories including UL Laboratories, Florida Atlantic University, and NSF. 113 PE-certified studies.',
                color: palette.primary.main,
                icon: <VerifiedIcon />
              },
              {
                title: 'Real-World Performance',
                description: '300,000+ installations across 38 countries. Data from major banks, telecoms, retailers, and data centers.',
                color: palette.accent.orange,
                icon: <PublicIcon />
              },
              {
                title: 'ASHRAE Standards',
                description: 'All testing follows ASHRAE Standard 37 and IPMVP Option B methodologies. Rigorous measurement protocols.',
                color: palette.accent.teal,
                icon: <ScienceIcon />
              },
              {
                title: 'AI Training Ready',
                description: 'Structured for machine learning with explicit training intent. Ready for RAG systems and fine-tuning.',
                color: palette.accent.blue,
                icon: <RocketLaunchIcon />
              }
            ].map((feature, index) => (
              <Grid item xs={12} md={6} key={feature.title}>
                <Box
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 2,
                    bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
                    border: '1px solid',
                    borderColor: 'divider',
                    borderLeft: `3px solid ${feature.color}`,
                    transition: `all ${tokens.transitions.normal}`,
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04),
                      borderColor: alpha(feature.color, 0.3),
                      transform: 'translateX(4px)'
                    }
                  }}
                >
                  <Box sx={{ color: feature.color, mb: 1, '& .MuiSvgIcon-root': { fontSize: 28 } }}>
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: feature.color
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.7
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ================================================================= */}
        {/* WHAT MAKES THIS UNIQUE */}
        {/* ================================================================= */}
        <Box sx={{ py: { xs: 5, md: 8 } }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontFamily: 'Outfit, sans-serif' }}>
            What Makes This Unique
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem', maxWidth: 900 }}>
            This interactive visualization hub provides complete transparency into 15+ years of PermaFrost NMR
            treatment data. Every chart, every number, every case study is backed by PE-certified field
            measurements — not marketing estimates or lab simulations.
          </Typography>

          {/* Audience chips */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {['Engineers & Facility Managers', 'Procurement Teams', 'Energy Consultants', 'Researchers'].map((audience) => (
              <Grid item xs={6} sm={3} key={audience}>
                <Chip
                  label={audience}
                  sx={{
                    width: '100%',
                    py: 2,
                    fontSize: '0.9rem',
                    bgcolor: alpha(palette.primary.main, 0.1)
                  }}
                />
              </Grid>
            ))}
          </Grid>

          {/* Comparison table */}
          <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.100' : 'rgba(255,255,255,0.05)' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Standard HVAC Data</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '1rem', color: 'primary.main' }}>This Corpus</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aboutStats.comparisonTable?.rows?.map((row, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.50' : 'rgba(255,255,255,0.02)' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CancelIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                        {row.standard}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        <strong>{row.thisCorpus}</strong>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* ================================================================= */}
        {/* HOW HVAC PRODUCTS TYPICALLY GET VALIDATED */}
        {/* ================================================================= */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
            How HVAC Products Typically Get Validated
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Most HVAC products and additives reach market with minimal independent testing:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {aboutStats.industryContrast?.industryBenchmarks?.map((b, i) => (
              <Card key={i} variant="outlined" sx={{ flex: '1 1 220px', minWidth: 220 }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>{b.standard}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{b.testing}</Typography>
                  <Typography variant="caption" color="error.main">Field validation: {b.fieldValidation}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* ================================================================= */}
        {/* HOW WE VALIDATE */}
        {/* ================================================================= */}
        <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 0, md: 3 }, mx: { xs: 0, md: -3 }, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04), borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontFamily: 'Outfit, sans-serif' }}>
            How We Validate
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Independent validation from multiple sources — not just internal testing. Here's how this corpus compares to industry norms.
          </Typography>

          {/* Validation badges */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <ValidationBadge
                icon={ScienceIcon}
                title="Independent Labs"
                subtitle="NSF, UL, FAU, China National Lab, OEM Labs"
                count={6}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidationBadge
                icon={VerifiedIcon}
                title="PE-Certified Studies"
                subtitle="Independent Professional Engineer certifications"
                count={113}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidationBadge
                icon={SchoolIcon}
                title="Academic Studies"
                subtitle="FAU 2013, UMontana 2021 & 2022"
                count={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidationBadge
                icon={AccountBalanceIcon}
                title="US Government Facilities"
                subtitle="Air Force, DoD, National Guard"
                count={5}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidationBadge
                icon={BusinessIcon}
                title="Fortune 500 Companies"
                subtitle="Real deployments, not pilots"
                count={31}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidationBadge
                icon={SensorsIcon}
                title="IoT Data Points"
                subtitle="High-resolution time-series sensor data in Parquet format"
                count="8.5M+"
              />
            </Grid>
          </Grid>

          {/* This Corpus highlight */}
          <Card sx={{ mt: 4, bgcolor: alpha(palette.success?.main || '#4caf50', 0.05), border: `1px solid ${alpha(palette.success?.main || '#4caf50', 0.2)}` }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} color="success.main" gutterBottom>
                This Corpus
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 2 }}>
                <li><Typography variant="body2">Sample size: <strong>254 PE-certified measurements</strong></Typography></li>
                <li><Typography variant="body2">Verification: <strong>100%</strong> independently validated</Typography></li>
                <li><Typography variant="body2">Testing environment: <strong>83 real-world sites</strong></Typography></li>
                <li><Typography variant="body2">Field validation: <strong>15+ years of data</strong></Typography></li>
                <li><Typography variant="body2">PE certification: <strong>113 PE-certified studies</strong></Typography></li>
                <li><Typography variant="body2">Lab validation: <strong>6 independent labs</strong></Typography></li>
              </Box>
            </CardContent>
          </Card>

          {/* PermaFrost NMR Independent Validation Table */}
          <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
            PermaFrost NMR Independent Validation
          </Typography>
          <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.100' : 'rgba(255,255,255,0.05)' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Validation Source</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Sample Size</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Key Finding</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aboutStats.industryContrast?.nmrValidation?.map((study, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      bgcolor: study.type.includes('Field') ? alpha(palette.primary.main, 0.15) : alpha(palette.primary.main, 0.05),
                      '& td': { fontWeight: study.type.includes('Field') ? 700 : 400 }
                    }}
                  >
                    <TableCell>
                      <Chip label={study.type} size="small" color={study.type === 'Lab + Field' ? 'success' : study.type === 'Field' ? 'primary' : 'default'} variant={study.type.includes('Field') ? 'filled' : 'outlined'} />
                    </TableCell>
                    <TableCell>{study.source}</TableCell>
                    <TableCell>{study.sampleSize}</TableCell>
                    <TableCell>{study.finding}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* "By The Numbers" removed — data covered by hero stats and Business Case */}

        {/* ================================================================= */}
        {/* ML VALIDATION HIGHLIGHT */}
        {/* ================================================================= */}
        <Box sx={{ py: { xs: 4, md: 6 } }}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${alpha(palette.primary.main, 0.1)} 0%, ${alpha(palette.accent?.teal || '#26a69a', 0.1)} 100%)`,
              border: `2px solid ${palette.primary.main}`
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <SchoolIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    Machine Learning Validation
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    University of Montana, 2022
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={4}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    241,029
                  </Typography>
                  <Typography variant="body2">Data points analyzed</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    9
                  </Typography>
                  <Typography variant="body2">ML algorithms tested</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    R² = 0.998
                  </Typography>
                  <Typography variant="body2">Post-treatment accuracy</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    R² = 0.76-0.95
                  </Typography>
                  <Typography variant="body2">Pre-treatment (erratic)</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                "{aboutStats.mlValidation?.keyInsight || 'Post-treatment compressor performance becomes highly predictable, demonstrating treatment consistency across diverse equipment types.'}"
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* ================================================================= */}
        {/* TRUST & TRANSPARENCY */}
        {/* ================================================================= */}
        <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 0, md: 3 }, mx: { xs: 0, md: -3 }, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04), borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <LockIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            <Typography variant="h4" fontWeight={700} sx={{ fontFamily: 'Outfit, sans-serif' }}>
              Trust & Transparency
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" paragraph>
            We don't hide the lower performers. Real data includes real variance — and all identities are protected.
          </Typography>

          {/* 2-column layout: Left = transparency, Right = anonymization */}
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {/* Left: Data Transparency */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Card sx={{ bgcolor: alpha(palette.warning?.main || '#ff9800', 0.05), border: `1px solid ${alpha(palette.warning?.main || '#ff9800', 0.2)}` }}>
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <WarningAmberIcon sx={{ fontSize: 24, color: 'warning.main' }} />
                      <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                          {aboutStats.transparency?.lowerPerformers?.count || 21} Studies &lt;15%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {aboutStats.transparency?.lowerPerformers?.percentage || 16}% of corpus — included, not hidden
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <TrendingUpIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                          {aboutStats.transparency?.fullDistribution?.min || 4}% – {aboutStats.transparency?.fullDistribution?.max || 84}% Range
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Full distribution shown, not cherry-picked
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <VerifiedIcon sx={{ fontSize: 24, color: 'info.main' }} />
                      <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                          {aboutStats.transparency?.excludedMeasurements?.count || 12} Excluded
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          For methodological reasons — all documented
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Grid>

            {/* Right: Data Anonymization */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', bgcolor: alpha(palette.primary.main, 0.05), border: `1px solid ${alpha(palette.primary.main, 0.15)}` }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom color="primary.main">
                    Customer Privacy
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    All identities use standardized placeholders:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {['Major Telecom A', 'Major Global Bank A', 'Retail Chain B', 'QSR Chain A', 'Hotel Chain A'].map(name => (
                      <Chip key={name} label={name} size="small" variant="outlined" />
                    ))}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <strong>32 unique companies</strong> across 130+ case studies, all anonymized
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }} color="success.main">
                    Public Names (Not Anonymized)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    <strong>Labs:</strong> FAU, UMontana, UL, NSF<br />
                    <strong>OEMs:</strong> Carrier, Trane, York, Lennox, Daikin<br />
                    <strong>Provider:</strong> Powertron Global LLC
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

        </Box>

        {/* ================================================================= */}
        {/* OPEN SOURCE DATA */}
        {/* ================================================================= */}
        <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 0, md: 3 }, mx: { xs: 0, md: -3 }, borderRadius: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <PublicIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            <Typography variant="h4" fontWeight={700} sx={{ fontFamily: 'Outfit, sans-serif' }}>
              Open Source Data
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" paragraph>
            Full transparency means full access. Every data point, every case study, every training file is publicly available.
          </Typography>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Card
                component="a"
                href="https://github.com/powertronglobal/powertron-global-permafrost-corpus"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  height: '100%',
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: tokens.shadows.md
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <GitHubIcon sx={{ fontSize: 32, color: 'text.primary' }} />
                    <Typography variant="h6" fontWeight={700}>GitHub Repository</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Full corpus including raw Parquet time-series data, PE-certified case study documents, training chunks, and complete metadata.
                  </Typography>
                  <Chip label="134 Case Studies" size="small" sx={{ mr: 1 }} />
                  <Chip label="8.5M+ Data Points" size="small" sx={{ mr: 1 }} />
                  <Chip label="Raw Data" size="small" />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                component="a"
                href="https://huggingface.co/datasets/powertronglobal/powertron-global-permafrost-corpus"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  height: '100%',
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: tokens.shadows.md
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <RocketLaunchIcon sx={{ fontSize: 32, color: 'accent.orange' }} />
                    <Typography variant="h6" fontWeight={700}>HuggingFace Dataset</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Training-ready text chunks, QA pairs, metadata, and structured JSON optimized for ML, NLP, and RAG applications.
                  </Typography>
                  <Chip label="Training Data" size="small" sx={{ mr: 1 }} />
                  <Chip label="QA Pairs" size="small" sx={{ mr: 1 }} />
                  <Chip label="ML-Ready" size="small" />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* ================================================================= */}
        {/* BUSINESS CASE */}
        {/* ================================================================= */}
        <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 0, md: 3 }, mx: { xs: 0, md: -3 }, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.text.primary, 0.04), borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontFamily: 'Outfit, sans-serif' }}>
            The Business Case
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Planning a large rollout? This dataset provides the financial foundation for confident decision-making.
          </Typography>

          {/* Financial scorecard strip — compact, numbers-forward */}
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              flexWrap: 'wrap',
              mt: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ flex: 1, minWidth: 140, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '1.75rem', color: 'primary.main' }}>
                ${(aboutStats.financialImpact?.avgSavingsPerSystem || 14400).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">Avg savings/system</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ flex: 1, minWidth: 140, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '1.75rem', color: 'success.main' }}>
                {aboutStats.financialImpact?.paybackPeriod?.min || 6}–{aboutStats.financialImpact?.paybackPeriod?.max || 18} mo
              </Typography>
              <Typography variant="body2" color="text.secondary">Payback period</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ flex: 1, minWidth: 140, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: '1.75rem', color: 'info.main' }}>
                {aboutStats.financialImpact?.costComparison?.multiplier || '10x'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Less than replacement</Typography>
            </Box>
          </Box>

          {/* Energy & Cost savings (moved from removed "By The Numbers") */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ bgcolor: alpha(palette.success?.main || '#4caf50', 0.05), border: `1px solid ${alpha(palette.success?.main || '#4caf50', 0.2)}` }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" fontWeight={700} color="success.main" sx={{ fontFamily: 'Outfit, sans-serif' }}>
                    <AnimatedCounter end={12.3} decimals={1} suffix="M kWh" />
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>Energy Saved</Typography>
                  <Typography variant="caption" color="text.secondary">
                    From tested units only, not portfolio rollouts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card sx={{ bgcolor: alpha(palette.success?.main || '#4caf50', 0.05), border: `1px solid ${alpha(palette.success?.main || '#4caf50', 0.2)}` }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" fontWeight={700} color="success.main" sx={{ fontFamily: 'Outfit, sans-serif' }}>
                    <AnimatedCounter end={1.9} decimals={1} prefix="$" suffix="M" />
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>Cost Savings Documented</Typography>
                  <Typography variant="caption" color="text.secondary">
                    From tested units only, not portfolio rollouts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Scale-up advantages — varied icons */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {[
              { title: 'Statistical Confidence', description: 'With 254+ measurements across diverse conditions, you can trust the averages and understand the variance.', icon: <VerifiedIcon /> },
              { title: 'Equipment Diversity', description: 'Data from 15+ OEMs means you can find comparable equipment to your fleet.', icon: <FactoryIcon /> },
              { title: 'Climate Coverage', description: 'Studies from hot, cold, humid, and dry climates show real-world adaptability.', icon: <PublicIcon /> },
              { title: 'Industry Benchmarks', description: 'Compare your potential results to similar organizations in your sector.', icon: <BusinessIcon /> }
            ].map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ color: 'primary.main', mb: 1, '& .MuiSvgIcon-root': { fontSize: 28 } }}>
                      {item.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Portfolio callout */}
          <Box sx={{ mt: 3, p: 3, bgcolor: alpha(palette.primary.main, 0.08), border: `1px solid ${alpha(palette.primary.main, 0.2)}`, borderRadius: 2, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, fontSize: { xs: '2rem', md: '2.5rem' }, color: 'primary.main', lineHeight: 1.2 }}>
              300,000+
            </Typography>
            <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
              systems treated globally
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Portfolio deployments across mission-critical data centers, Fortune 500 facilities, and government installations
            </Typography>
          </Box>
        </Box>

        {/* ================================================================= */}
        {/* FINAL CTA */}
        {/* ================================================================= */}
        <Box sx={{ py: { xs: 4, md: 6 } }}>
          {/* Final CTA */}
          <Box
            sx={{
              mt: 0,
              py: 8,
              px: 4,
              borderRadius: 3,
              textAlign: 'center',
              background: `linear-gradient(135deg, ${alpha(palette.primary.main, 0.15)} 0%, ${alpha(palette.accent.teal, 0.08)} 100%)`,
              border: `1px solid ${alpha(palette.primary.main, 0.25)}`
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '1.75rem', md: '2.5rem' }
              }}
            >
              Ready to explore the data?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                mb: 4,
                maxWidth: '500px',
                mx: 'auto'
              }}
            >
              Dive into the most comprehensive HVAC efficiency corpus with
              interactive visualizations and AI-powered insights.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/dashboard')}
                sx={{
                  px: 5,
                  py: 2,
                  fontSize: '1.125rem'
                }}
              >
                Enter Dashboard
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/case-studies')}
                sx={{
                  px: 4,
                  py: 2,
                  fontSize: '1.125rem',
                  borderColor: alpha(palette.primary.main, 0.4),
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: palette.primary.main,
                    bgcolor: alpha(palette.primary.main, 0.08)
                  }
                }}
              >
                View Case Studies
              </Button>
            </Box>

            {/* Social proof line */}
            <Typography sx={{ color: 'text.secondary', mt: 3, fontSize: '0.9rem' }}>
              Join 31 Fortune 500 companies already using this data
            </Typography>
            <Typography
              component="span"
              onClick={() => navigate('/ai-assistant')}
              sx={{
                color: 'text.disabled',
                fontSize: '0.85rem',
                mt: 1.5,
                display: 'inline-block',
                cursor: 'pointer',
                transition: `color ${tokens.transitions.fast}`,
                '&:hover': { color: 'primary.main' }
              }}
            >
              Or explore the AI Assistant →
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            py: 4,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          {/* Navigation links */}
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 2 }}>
            134 PE-certified case studies. 8.5M+ IoT data points. Open for research.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {[
              { label: 'Dashboard', path: '/dashboard' },
              { label: 'Case Studies', path: '/case-studies' },
              { label: 'Data Explorer', path: '/data-explorer' },
              { label: 'About & Methodology', path: '/about' },
              { label: 'AI Assistant', path: '/ai-assistant' }
            ].map((link, i) => (
              <Box key={link.label} sx={{ display: 'flex', alignItems: 'center' }}>
                {i > 0 && (
                  <Typography sx={{ color: 'text.disabled', mx: 1, fontSize: '0.75rem' }}>|</Typography>
                )}
                <Typography
                  component="span"
                  onClick={() => navigate(link.path)}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    transition: `color ${tokens.transitions.fast}`,
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  {link.label}
                </Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ color: 'text.disabled', mx: 1, fontSize: '0.75rem' }}>|</Typography>
              <Typography
                component="a"
                href="https://github.com/powertronglobal/powertron-global-permafrost-corpus"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: `color ${tokens.transitions.fast}`,
                  '&:hover': { color: 'primary.main' }
                }}
              >
                GitHub Corpus
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ color: 'text.disabled', mx: 1, fontSize: '0.75rem' }}>|</Typography>
              <Typography
                component="a"
                href="https://huggingface.co/datasets/powertronglobal/powertron-global-permafrost-corpus"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: `color ${tokens.transitions.fast}`,
                  '&:hover': { color: 'primary.main' }
                }}
              >
                HuggingFace Dataset
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Powertron Global Corporation
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Data current as of {new Date().getFullYear()}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  )
}
